/**
 * Chart Data Builder
 * 
 * Functions to build chart data series from real backend data
 * and generate scenario projections
 */

import { TIMELINE_EVENTS, mapTimestampToChapter } from './timelineConfig';

/**
 * Convert ISO date to date-only string (YYYY-MM-DD)
 * @param {Date} date - The date object
 * @returns {string} - ISO date string (YYYY-MM-DD)
 */
function toISODateString(date) {
  return date.toISOString().split('T')[0];
}

/**
 * Build observed data series from backend historical data
 * @param {Array} historicalData - Raw data from backend
 * @returns {Array} - Formatted data for chart
 */
export function buildObservedSeries(historicalData) {
  if (!historicalData || historicalData.length === 0) {
    return [];
  }
  
  return historicalData.map(record => ({
    date: record.date,
    dateObj: new Date(record.date),
    raceIndex: record.raceIndex,
    slowdownIndex: record.slowdownIndex,
    chapter: mapTimestampToChapter(record.date),
    isObserved: true
  }));
}

/**
 * Build scenario projection lines
 * These are conceptual futures, not real data
 * 
 * @param {Array} observedData - The observed data series
 * @returns {Object} - Object with racingScenario and slowdownScenario arrays
 */
export function buildScenarioProjections(observedData) {
  if (!observedData || observedData.length === 0) {
    return { racingScenario: [], slowdownScenario: [] };
  }
  
  // Get the last observed data point
  const lastObserved = observedData[observedData.length - 1];
  const lastDate = new Date(lastObserved.date);
  const lastRaceIndex = lastObserved.raceIndex;
  const lastSlowdownIndex = lastObserved.slowdownIndex;
  
  // Get bifurcation point (E15, October 2027)
  // const bifurcationEvent = getBifurcationPoint();
  // We want to show projections from "NOW" regardless of bifurcation date
  // to show hypothetical futures to the user.
  // The bifurcation point is narrative, but the projections guide the user.
  
  // Create projection dates (monthly intervals for next 15 months after last observed)
  const projectionMonths = 15;
  const racingScenario = [];
  const slowdownScenario = [];
  
  // Start from last observed point
  racingScenario.push({
    date: lastObserved.date,
    dateObj: lastDate,
    raceIndex: lastRaceIndex,
    slowdownIndex: lastSlowdownIndex,
    isProjection: true,
    scenario: 'racing'
  });
  
  slowdownScenario.push({
    date: lastObserved.date,
    dateObj: lastDate,
    raceIndex: lastRaceIndex,
    slowdownIndex: lastSlowdownIndex,
    isProjection: true,
    scenario: 'slowdown'
  });
  
  // Projection growth/decay rates (per month)
  // These are simple linear trends for communication, not prediction
  const RACING_RACE_GROWTH = 0.08;      // Race accelerates in racing scenario
  const RACING_SLOWDOWN_DECAY = 0.05;   // Governance weakens in racing scenario
  const SLOWDOWN_RACE_DECAY = 0.06;     // Race decelerates in slowdown scenario
  const SLOWDOWN_SLOWDOWN_GROWTH = 0.07; // Governance strengthens in slowdown scenario
  
  // Generate projections
  for (let i = 1; i <= projectionMonths; i++) {
    const projDate = new Date(lastDate);
    projDate.setMonth(projDate.getMonth() + i);
    
    // Racing scenario: Race Index increases, Slowdown Index decreases
    const racingRaceIndex = Math.min(3.0, lastRaceIndex + (i * RACING_RACE_GROWTH));
    const racingSlowdownIndex = Math.max(0.3, lastSlowdownIndex - (i * RACING_SLOWDOWN_DECAY));
    
    racingScenario.push({
      date: toISODateString(projDate),
      dateObj: projDate,
      raceIndex: parseFloat(racingRaceIndex.toFixed(2)),
      slowdownIndex: parseFloat(racingSlowdownIndex.toFixed(2)),
      isProjection: true,
      scenario: 'racing'
    });
    
    // Slowdown scenario: Race Index decreases, Slowdown Index increases
    const slowdownRaceIndex = Math.max(0.5, lastRaceIndex - (i * SLOWDOWN_RACE_DECAY));
    const slowdownSlowdownIndex = Math.min(3.0, lastSlowdownIndex + (i * SLOWDOWN_SLOWDOWN_GROWTH));
    
    slowdownScenario.push({
      date: toISODateString(projDate),
      dateObj: projDate,
      raceIndex: parseFloat(slowdownRaceIndex.toFixed(2)),
      slowdownIndex: parseFloat(slowdownSlowdownIndex.toFixed(2)),
      isProjection: true,
      scenario: 'slowdown'
    });
  }
  
  return { racingScenario, slowdownScenario };
}

/**
 * Build event annotations for the chart
 * @param {Date} startDate - Start of visible range
 * @param {Date} endDate - End of visible range
 * @returns {Array} - Array of event annotations
 */
export function buildEventAnnotations(startDate, endDate) {
  return TIMELINE_EVENTS
    .filter(event => event.date >= startDate && event.date <= endDate)
    .map(event => ({
      ...event,
      x: toISODateString(event.date)
    }));
}

/**
 * Filter data based on time range
 * @param {Array} data - Data array to filter
 * @param {string} timeRange - Time range selector ('1m', '3m', '6m', 'all')
 * @returns {Array} - Filtered data
 */
export function filterDataByTimeRange(data, timeRange) {
  if (timeRange === 'all' || !data || data.length === 0) {
    return data;
  }
  
  const now = new Date();
  const cutoffDate = new Date();
  
  switch(timeRange) {
    case '1m':
      cutoffDate.setMonth(now.getMonth() - 1);
      break;
    case '3m':
      cutoffDate.setMonth(now.getMonth() - 3);
      break;
    case '6m':
      cutoffDate.setMonth(now.getMonth() - 6);
      break;
    default:
      return data;
  }
  
  return data.filter(d => {
    const date = d.dateObj || new Date(d.date);
    return date >= cutoffDate;
  });
}

/**
 * Format date for display in the chart
 * @param {string} dateStr - ISO date string
 * @param {boolean} includeYear - Whether to include year
 * @returns {string} - Formatted date
 */
export function formatDateForDisplay(dateStr, includeYear = false) {
  const date = new Date(dateStr);
  const options = includeYear 
    ? { month: 'short', day: 'numeric', year: 'numeric' }
    : { month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
}
